import React, { useEffect, useState } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFTextArea from '../../../controls/tempRHFControls/RHFTextArea';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import EmployeeBenefitCodes from 'src/models/personnel/EmployeeBenefitCodes';
import EmployeeBenefits from 'src/models/personnel/EmployeeBenefits';

interface IAddBenefitDialogProps {
    dialogName: string;
    hideDialog: () => void;
    addBenefit: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
    benefitCodes: EmployeeBenefitCodes;
    lbId: number;
    resetTrigger: number;
}

// Mirrors Oracle's Benefit_Add popup - it only ever inserts an "Additional" (code 'AD'),
// flat-rate (method 'F'), optional (cat_req 'OPT') line, which is why there's no
// Code/Method field here the way there is on the Edit Benefit dialog.
const BENEFIT_TYPE_OPTIONS = [
    { value: 'INS', label: 'Insurance' },
    { value: 'WC', label: 'Worker Comp' },
    { value: 'PTO', label: 'Paid Time Off' },
    { value: 'TAX', label: 'Taxes' },
    { value: 'RET', label: 'Retirement' },
];

const TYPE_TO_CODE_BUCKET: Record<string, keyof EmployeeBenefitCodes> = {
    INS: 'insurance',
    WC: 'workersComp',
    PTO: 'pto',
    TAX: 'tax',
    RET: 'retirement',
};

const CATEGORY_LABELS: Record<string, string> = {
    HEALTH: 'Health',
    DENTAL: 'Dental',
    VISION: 'Vision',
    LIFE: 'Life',
    DISABILITY: 'Disability',
    VACATION: 'Vacation',
    PERSONAL: 'Personal',
    HOLIDAY: 'Holiday',
    '401K': '401K',
    FICA: 'FICA',
    WORKCOMP: 'Workers Comp',
};

const AddBenefitDialog = (props: IAddBenefitDialogProps) => {
    const { register, watch, getValues, setValue, reset } = useForm({
        defaultValues: { type: 'INS', category: '', flatAmount: '', notes: '' },
    });
    const { post, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);

    const watchType = watch('type');

    // Dialog.tsx keeps this component mounted and toggles visibility, so react-hook-form's
    // state survives between opens - reset explicitly whenever the parent signals a fresh open.
    useEffect(() => {
        reset({ type: 'INS', category: '', flatAmount: '', notes: '' });
    }, [props.resetTrigger]);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const categoryOptions = () => {
        const bucket = props.benefitCodes?.[TYPE_TO_CODE_BUCKET[watchType]] ?? [];
        const uniqueCategories = Array.from(new Set(bucket.map((code) => code.category)));
        return uniqueCategories.map((category) => ({ value: category, label: CATEGORY_LABELS[category] ?? category }));
    };

    const onTypeChange = () => {
        setValue('category', '');
    };

    const handleSubmit = async () => {
        const values = getValues();

        if (!values.category) {
            showToast('A Benefit Category must be defined before adding a benefit.', `rgba(255,0,0,0.5)`);
            return;
        }

        if (!values.flatAmount || parseFloat(values.flatAmount) <= 0) {
            showToast('An Annual Flat Rate must be defined before adding a benefit.', `rgba(255,0,0,0.5)`);
            return;
        }

        const newBenefit = {
            id: props.lbId,
            type: values.type,
            category: values.category,
            flatAmount: parseFloat(values.flatAmount),
            benefitNotes: values.notes,
        } as EmployeeBenefits;

        await post(`/v1.0/landmen/benefits/add-employee-benefit-detail`, newBenefit);

        if (response.ok) {
            props.showToast('Successfully added benefit');
            props.addBenefit();
        } else {
            showToast('Unable to add benefit: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    return (
        <Dialog title="Add Benefit" dialogName={props.dialogName} bodyStyle={{ width: '25%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <form>
                    <RHFSelect
                        name={'type'}
                        label={'Benefit Type'}
                        labelOnLeft={true}
                        register={register}
                        isRequired={true}
                        options={BENEFIT_TYPE_OPTIONS}
                        onChange={() => onTypeChange()}
                        spaceLabelContent={true}
                    />
                    <RHFSelect
                        name={'category'}
                        label={'Category'}
                        labelOnLeft={true}
                        register={register}
                        isRequired={true}
                        options={categoryOptions()}
                        spaceLabelContent={true}
                    />
                    <RHFInputBox
                        name={'flatAmount'}
                        label={'Annual Flat Rate'}
                        labelOnLeft={true}
                        register={register}
                        isRequired={true}
                        type="number"
                        spaceLabelContent={true}
                    />
                    <div className="form-row">
                        <label>Notes</label>
                        <RHFTextArea
                            name={'notes'}
                            register={register}
                            rows={3}
                        />
                    </div>
                </form>
            </div>
            <div className="button-row" style={{ paddingBottom: '1rem' }}>
                <Button text="OK" onClick={handleSubmit} intent="primary" />
                <Button text="Cancel" onClick={() => props.hideDialog()} intent="secondary" />
            </div>
        </Dialog>
    );
};

export default AddBenefitDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/AddBenefitDialog.tsx